import UsageLimitAlertService from './UsageLimitAlertService';

/**
 * AlertManager - Central coordination for all alert types
 * Future-ready for system alerts, account alerts, security alerts, etc.
 */
class AlertManager {

    // Alert types registry
    static ALERT_TYPES = {
        USAGE_LIMIT: 'usage-limit',
        SYSTEM: 'system',
        ACCOUNT: 'account',
        SECURITY: 'security',
        ACTIVITY: 'activity'
    };

    /**
     * Get all active alerts for a tenant
     * @param {Object} context - Context object with usage data, tenant info, etc.
     * @returns {Array} Array of alert objects
     */
    static async getAllAlerts(context = {}) {
        const alerts = [];

        try {
            // Usage Limit Alerts
            if (context.usageData) {
                const usageAlerts = this.getUsageLimitAlerts(context.usageData, context.tenantId);
                alerts.push(...usageAlerts);
            }

            // System Alerts (demo for testing)
            if (context.forceCheck) {
                const systemAlerts = this.getSystemAlerts(context.tenantId);
                alerts.push(...systemAlerts);
            }

            // Future alert types can be added here:
            // const systemAlerts = await this.getSystemAlerts();
            // const accountAlerts = await this.getAccountAlerts(context.tenantId);
            // alerts.push(...systemAlerts, ...accountAlerts);

            // Sort by severity and timestamp
            return this.sortAlerts(alerts);

        } catch (error) {
            console.error('Error getting alerts:', error);
            return [];
        }
    }

    /**
     * Get usage limit alerts specifically
     */
    static getUsageLimitAlerts(usageData, tenantId = 'default') {
        if (!usageData?.summary) return [];

        const currentUsage = usageData.summary.totalSessions || 0;
        const limit = usageData.summary.chatLimit || 500;
        const planType = usageData.summary.planType || 'nano';

        const alerts = UsageLimitAlertService.checkUsageAlerts(currentUsage, limit, planType);

        // Filter out dismissed alerts
        return alerts.filter(alert =>
            UsageLimitAlertService.shouldShowAlert(alert.id, tenantId)
        );
    }

    /**
     * Get system alerts (demo for testing)
     * @param {string} tenantId - Tenant identifier
     * @returns {Array} Array of system alerts
     */
    static getSystemAlerts(tenantId = 'default') {
        const alerts = [];

        // Demo system alert for testing (you can remove this later)
        const demoAlert = {
            id: 'system-demo-001',
            type: this.ALERT_TYPES.SYSTEM,
            severity: 'info',
            title: 'Scheduled Maintenance',
            message: 'System maintenance scheduled for tonight at 2:00 AM EST. Expected downtime: 30 minutes.',
            timestamp: Date.now(),
            actionLabel: 'Learn More',
            action: () => console.log('System maintenance info clicked'),
            data: { maintenanceTime: '2:00 AM EST' }
        };

        // Only show if not dismissed
        if (!this.isAlertDismissed('system-demo-001', this.ALERT_TYPES.SYSTEM, tenantId)) {
            alerts.push(demoAlert);
        }

        return alerts;
    }

    /**
     * Dismiss an alert
     */
    static dismissAlert(alertId, alertType, tenantId = 'default') {
        switch (alertType) {
            case this.ALERT_TYPES.USAGE_LIMIT:
                UsageLimitAlertService.dismissAlert(alertId, tenantId);
                break;
            case this.ALERT_TYPES.SYSTEM:
                // Store dismissed system alerts in localStorage
                const dismissedKey = `dismissed_system_alerts_${tenantId}`;
                const dismissed = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
                if (!dismissed.includes(alertId)) {
                    dismissed.push(alertId);
                    localStorage.setItem(dismissedKey, JSON.stringify(dismissed));
                }
                break;
            default:
                console.warn(`Unknown alert type: ${alertType}`);
        }
    }

    /**
     * Check if an alert has been dismissed
     */
    static isAlertDismissed(alertId, alertType, tenantId = 'default') {
        switch (alertType) {
            case this.ALERT_TYPES.USAGE_LIMIT:
                return !UsageLimitAlertService.shouldShowAlert(alertId, tenantId);
            case this.ALERT_TYPES.SYSTEM:
                const dismissedKey = `dismissed_system_alerts_${tenantId}`;
                const dismissed = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
                return dismissed.includes(alertId);
            default:
                return false;
        }
    }

    /**
     * Sort alerts by priority and timestamp
     */
    static sortAlerts(alerts) {
        const severityOrder = { error: 3, warning: 2, info: 1, success: 0 };

        return alerts.sort((a, b) => {
            // First by severity (highest first)
            const severityDiff = (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
            if (severityDiff !== 0) return severityDiff;

            // Then by timestamp (newest first)
            return new Date(b.timestamp) - new Date(a.timestamp);
        });
    }

    /**
     * Get alert summary for display
     */
    static getAlertSummary(alerts) {
        const summary = {
            total: alerts.length,
            byType: {},
            bySeverity: {
                error: 0,
                warning: 0,
                info: 0,
                success: 0
            },
            hasHigh: false,
            hasCritical: false
        };

        alerts.forEach(alert => {
            // Count by type
            summary.byType[alert.type] = (summary.byType[alert.type] || 0) + 1;

            // Count by severity
            summary.bySeverity[alert.severity]++;

            // Set flags
            if (alert.severity === 'error') summary.hasCritical = true;
            if (alert.severity === 'warning' || alert.severity === 'error') summary.hasHigh = true;
        });

        return summary;
    }

    /**
     * Format alert for display
     */
    static formatAlertForDisplay(alert) {
        return {
            ...alert,
            formattedTimestamp: new Date(alert.timestamp).toLocaleString(),
            icon: this.getAlertIcon(alert.severity, alert.type),
            displaySeverity: alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)
        };
    }

    /**
     * Get appropriate icon for alert
     */
    static getAlertIcon(severity, type) {
        if (type === this.ALERT_TYPES.USAGE_LIMIT) {
            switch (severity) {
                case 'error': return '🚨';
                case 'warning': return '⚠️';
                case 'info': return '📊';
                default: return '📈';
            }
        }

        // Default icons by severity
        switch (severity) {
            case 'error': return '❌';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            case 'success': return '✅';
            default: return '📢';
        }
    }

    /**
     * Cleanup old data for all alert types
     */
    static cleanup(tenantId = 'default') {
        UsageLimitAlertService.cleanupDismissedAlerts(tenantId);
        // Add cleanup for other alert types as they're implemented
    }
}

export default AlertManager;
